/**
 * `accueilStore`, ⟦C27 · A-153⟧ le fil qui tient l'accueil (`screens/Accueil/Accueil.tsx`)
 * PAR-DESSUS la chambre, à la création d'une chaîne, exactement comme `stores/introStore.ts`
 * tient l'intro par-dessus la même bascule de routeur (voir son commentaire pour le pourquoi :
 * `runStore.creer()` fait basculer `Routeur` vers `<Studio/>` à la milliseconde où il résout, et
 * démonte tout ce qui aurait été monté SOUS l'écran de création).
 *
 * Deux stores distincts plutôt qu'un seul plus gros : `introStore` reste propre à l'animation
 * d'ouverture de chaîne (§C22), celui-ci propre à l'accueil qui la suit directement (§C27),
 * `App.tsx` enchaîne l'un sur l'autre dans le `onTermine` de `<Intro>`.
 *
 * **Trois temps, jamais quatre** :
 * 1. `actif && !glissement`, les 4 répliques jouent, plein écran, aucune interface montée
 *    (`Studio.tsx` rend `null` tant que c'est vrai, voir son commentaire).
 * 2. `actif && glissement`, le glissement a commencé (`Accueil.tsx` anime sa propre sortie,
 *    `Studio.tsx` monte l'interface réelle avec son entrée en cascade, `.interface-arrivee`).
 * 3. `!actif`, l'accueil a disparu, la chambre est l'écran normal, pour toujours (`terminer()`
 *    ne se rappelle de rien : reprendre une partie ne remet jamais `actif` à `true`).
 */

import { create } from "zustand";

import type { GenreAvatar } from "@maxime/shared";

import { useDecouverteStore } from "./decouverteStore";

interface AccueilStore {
  /** Une intro doit jouer : montée par `App.tsx` tant que c'est vrai. */
  actif: boolean;
  genre: GenreAvatar | null;
  /** `true` dès que la sortie (le glissement, §3 du chantier) a commencé. */
  glissement: boolean;

  /** Appelé par `App.tsx` dans le `onTermine` de `<Intro>` : l'accueil prend le relais. */
  demarrer(genre: GenreAvatar): void;
  /** La dernière réplique est allée à son terme (▶) ou « Passer » a été activé : lance la
   *  sortie, jamais l'inverse (`terminer()` seul, sans `glissement`, sauterait l'animation). */
  lancerGlissement(): void;
  /** Le glissement est terminé : l'accueil se retire pour de bon. */
  terminer(): void;
}

export const useAccueilStore = create<AccueilStore>((set) => ({
  actif: false,
  genre: null,
  glissement: false,

  demarrer(genre) {
    set({ actif: true, genre, glissement: false });
  },
  lancerGlissement() {
    // ⟦C27 · A-153⟧ L'accueil vient de dire les 4 répliques du briefing (ou l'a sauté, « Passer ») :
    // la leçon `briefing` (`game/decouverte.ts`, `quand: () => true`, jamais vue pour une chaîne
    // neuve) est marquée vue ICI, au moment où l'accueil cède la place, pour qu'elle ne se
    // rejoue jamais en double dès que l'interface arrive (`useMattChambre.ts` gate déjà tout le
    // reste tant que `actif` est vrai, cette ligne couvre l'instant D'APRÈS).
    useDecouverteStore.getState().marquerVue("briefing");
    set({ glissement: true });
  },
  terminer() {
    set({ actif: false, glissement: false });
  },
}));
